// Store Locator (Leaflet)
// Expects:
// - Leaflet loaded globally (window.L)
// - <div id="map" data-pin-url="..." data-pin-size="40,45" data-config-script-id="store-locator-config"></div>
(function () {
  function init() {
    const el = document.getElementById('map');
    if (!el) return;
    if (typeof window.L === 'undefined') return;
    if (el.dataset.initialized === 'true') return;
    el.dataset.initialized = 'true';

    const L = window.L;
    const pinUrl = el.dataset.pinUrl || '';
    const logoUrl = el.dataset.logoUrl || '';
    const fitBoundsToRetailers = el.dataset.fitBounds === 'true';
    const websiteLinkText = el.dataset.websiteLinkText || 'Visit website';
    const distanceSuffixText = el.dataset.distanceText || 'miles away';
    const sizeParts = (el.dataset.pinSize || '40,45').split(',').map((n) => Number(n.trim()));
    const iconSize = sizeParts.length === 2 && sizeParts.every((n) => Number.isFinite(n)) ? sizeParts : [40, 45];
    const configScriptId = el.dataset.configScriptId || 'store-locator-config';

    let config = null;
    const configEl = document.getElementById(configScriptId);
    if (configEl && configEl.textContent) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        config = null;
      }
    }

    const defaultLat = Number(config?.territory?.defaultLat ?? 53.0766);
    const defaultLng = Number(config?.territory?.defaultLng ?? -1.5447);
    const defaultZoom = Number(config?.territory?.defaultZoom ?? 10);
    const retailers = Array.isArray(config?.retailers) ? config.retailers : [];

    const map = L.map('map', {
      scrollWheelZoom: false,
      zoomControl: false,
    }).setView([defaultLat, defaultLng], defaultZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const pinIcon = pinUrl
      ? L.icon({
          iconUrl: pinUrl,
          iconSize,
          iconAnchor: [Math.round(iconSize[0] / 2), iconSize[1]],
        })
      : null;

    function fieldToString(v) {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number' || typeof v === 'boolean') return String(v);
      if (typeof v === 'object') {
        // Shopify address-like objects (can appear as plain object or nested under .value)
        const addrFromObject = (obj) => {
          if (!obj || typeof obj !== 'object') return '';
          const parts = [
            obj.address1 || obj.address_1 || obj.line1,
            obj.address2 || obj.address_2 || obj.line2,
            obj.city,
            obj.province || obj.state || obj.region,
            obj.zip || obj.postalCode || obj.postcode,
            obj.country,
          ].filter(Boolean);
          return parts.join(', ');
        };

        const addrDirect = addrFromObject(v);
        if (addrDirect) return addrDirect;

        if (typeof v.value === 'string') return v.value;
        if (typeof v.value === 'number' || typeof v.value === 'boolean') return String(v.value);
        const addrNested = addrFromObject(v.value);
        if (addrNested) return addrNested;
      }
      return '';
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function haversineMiles(aLat, aLng, bLat, bLng) {
      const toRad = (d) => (d * Math.PI) / 180;
      const R = 6371000; // meters
      const dLat = toRad(bLat - aLat);
      const dLng = toRad(bLng - aLng);
      const lat1 = toRad(aLat);
      const lat2 = toRad(bLat);
      const s =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
      const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
      const meters = R * c;
      return meters / 1609.344;
    }

    const chevronSvg =
      '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
      '</svg>';

    const bounds = [];
    const markerRefs = [];
    retailers.forEach((r) => {
      const lat = Number(r.lat);
      const lng = Number(r.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const marker = pinIcon ? L.marker([lat, lng], { icon: pinIcon }) : L.marker([lat, lng]);

      const title = fieldToString(r.name);
      const url = fieldToString(r.url);
      const address = fieldToString(r.address);
      const city = fieldToString(r.city);
      const addressBits = [address, city].filter(Boolean).join(', ');

      if (title || url || addressBits) {
        const safeTitle = title || 'Retailer';
        const safeTitleHtml = escapeHtml(safeTitle);
        const urlHtml = escapeHtml(url);
        const link = url ? `<a href="${urlHtml}" target="_blank" rel="noopener">${safeTitleHtml}</a>` : safeTitleHtml;
        const extra = addressBits ? `<div style="margin-top:4px;">${escapeHtml(addressBits)}</div>` : '';
        const cta = url
          ? `<div style="margin-top:8px;">
              <a href="${urlHtml}" target="_blank" rel="noopener" style="text-decoration:underline;font-weight:600;">
                ${escapeHtml(websiteLinkText)}
              </a>
            </div>`
          : '';
        // Always render the logo container so it's visible in the DOM for debugging,
        // even if the image fails to load.
        const logo = `<div style="width:44px;height:44px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.9);overflow:hidden;">
          <img
            src="${logoUrl}"
            alt=""
            style="display:block;max-width:36px;max-height:36px;object-fit:contain;"
            onerror="this.style.display='none';this.parentElement.style.display='none';" />
        </div>`;

        marker.bindPopup(
          `<div style="display:flex;gap:12px;align-items:center;">
            ${logoUrl ? `<div style="flex:0 0 auto;">${logo}</div>` : ''}
            <div style="min-width:180px;line-height:1.25;">
              <div style="font-weight:600;line-height:1.2;">${link}</div>
              ${extra}
              ${cta}
            </div>
          </div>`,
          { autoPan: false }
        );
      }

      marker.addTo(map);
      bounds.push([lat, lng]);
      markerRefs.push({ marker, lat, lng, retailer: r });
    });

    if (bounds.length && fitBoundsToRetailers) {
      map.fitBounds(bounds, { padding: [40, 40] });
    } else {
      const fallbackLatLng = [defaultLat, defaultLng];
      (pinIcon ? L.marker(fallbackLatLng, { icon: pinIcon }) : L.marker(fallbackLatLng)).addTo(map);
    }

    // Sidebar list
    const retailersEl = document.getElementById('retailers');
    const listItems = markerRefs.map((m, idx) => ({
      idx,
      lat: m.lat,
      lng: m.lng,
      marker: m.marker,
      name: fieldToString(m.retailer.name),
      address: fieldToString(m.retailer.address),
      city: fieldToString(m.retailer.city),
    }));

    let lastUserPos = null;
    let searchQuery = '';

    function scrollToMapIfNeeded() {
      const mapEl = document.getElementById('map');
      if (!mapEl) return;

      // Only auto-scroll on mobile + tablet
      if (!window.matchMedia || !window.matchMedia('(max-width: 1199px)').matches) return;

      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReduced ? 'auto' : 'smooth';

      const top = mapEl.getBoundingClientRect().top + window.pageYOffset;
      const offset = 110; // account for sticky header
      window.scrollTo({ top: Math.max(0, top - offset), behavior });
    }

    function ensureRetailersSidebarShell() {
      if (!retailersEl) return null;
      if (retailersEl.dataset.retailersShell === '1') {
        return {
          actionsEl: retailersEl.querySelector('[data-retailers-actions]'),
          rowsEl: retailersEl.querySelector('[data-retailers-rows]'),
          searchEl: retailersEl.querySelector('[data-retailer-search]'),
        };
      }

      retailersEl.innerHTML = `
        <div class="retailers-search-row">
          <div class="retailers-search">
            <input
              type="search"
              inputmode="search"
              autocomplete="off"
              spellcheck="false"
              placeholder=""
              data-retailer-search />
          </div>
          <div class="retailers-actions" data-retailers-actions></div>
        </div>
        <div class="retailers-rows" data-retailers-rows></div>
      `;

      retailersEl.dataset.retailersShell = '1';

      const rowsEl = retailersEl.querySelector('[data-retailers-rows]');
      rowsEl.addEventListener('click', (e) => {
        const row = e.target.closest('[data-retailer-idx]');
        if (!row) return;
        const idx = Number(row.getAttribute('data-retailer-idx'));
        const ref = markerRefs[idx];
        if (!ref) return;

        scrollToMapIfNeeded();

        const zoomTo = Math.max(defaultZoom, 12);
        map.once('moveend', () => ref.marker.openPopup());
        if (map.flyTo) {
          map.flyTo([ref.lat, ref.lng], zoomTo, { animate: true, duration: 0.6 });
        } else {
          map.setView([ref.lat, ref.lng], zoomTo, { animate: true });
        }
      });

      const searchEl = retailersEl.querySelector('[data-retailer-search]');
      searchEl.addEventListener('input', () => {
        searchQuery = searchEl.value || '';
        renderRetailersList(lastUserPos);
      });

      return {
        actionsEl: retailersEl.querySelector('[data-retailers-actions]'),
        rowsEl,
        searchEl,
      };
    }

    function renderRetailersList(userPos) {
      if (!retailersEl) return;
      lastUserPos = userPos;

      const shell = ensureRetailersSidebarShell();
      if (!shell) return;

      const items = listItems
        .map((it) => {
          let miles = null;
          if (userPos) {
            miles = haversineMiles(userPos.lat, userPos.lng, it.lat, it.lng);
          }
          return { ...it, miles };
        })
        .sort((a, b) => {
          if (a.miles == null && b.miles == null) return 0;
          if (a.miles == null) return 1;
          if (b.miles == null) return -1;
          return a.miles - b.miles;
        });

      const q = (searchQuery || '').trim().toLowerCase();
      const filtered = q
        ? items.filter((it) => {
            const name = (it.name || '').toLowerCase();
            const address = (it.address || '').toLowerCase();
            return name.includes(q) || address.includes(q);
          })
        : items;

      shell.searchEl.value = searchQuery;

      const rows = filtered
        .map((it) => {
          const distanceText =
            it.miles == null
              ? `— ${escapeHtml(distanceSuffixText)}`
              : `${it.miles.toFixed(it.miles < 10 ? 1 : 0)} ${escapeHtml(distanceSuffixText)}`;

          const nameHtml = escapeHtml(it.name || 'Retailer');
          const addressLine = escapeHtml(it.address || '');
          const addressHtml = addressLine
            ? `<div class="retailer-address">${addressLine}</div>`
            : `<div class="retailer-address">Address unavailable</div>`;

          return `
            <div class="retailer-row" data-retailer-idx="${it.idx}">
              <div style="min-width:0;">
                <div class="retailer-title">${nameHtml}</div>
                ${addressHtml}
                <div class="retailer-distance" style="margin-top:6px;">${distanceText}</div>
              </div>
              <div class="retailer-meta">
                <div class="retailer-chevron">${chevronSvg}</div>
              </div>
            </div>
          `;
        })
        .join('');

      const buttonHtml =
        userPos
          ? ''
          : `<button type="button" data-enable-location style="padding:10px 12px;border-radius:10px;border:1px solid rgba(248,246,240,0.25);background:rgba(255,255,255,0.06);color:#f8f6f0;cursor:pointer;white-space:nowrap;">
            ${locationButtonText}
            </button>`;

      shell.actionsEl.innerHTML = buttonHtml;
      shell.rowsEl.innerHTML =
        filtered.length === 0
          ? `<div class="retailer-address">No retailers match “${escapeHtml(searchQuery.trim())}”.</div>`
          : rows;

      const enableBtn = shell.actionsEl.querySelector('[data-enable-location]');
      if (enableBtn) enableBtn.addEventListener('click', requestUserLocation);
    }

    function requestUserLocation() {
      if (!navigator.geolocation) {
        renderRetailersList(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          renderRetailersList(userPos);
        },
        () => {
          renderRetailersList(null);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    }

    // Render immediately (no prompt), user can opt-in via button
    renderRetailersList(null);

    const fixTiles = () => map.invalidateSize();
    setTimeout(fixTiles, 0);
    setTimeout(fixTiles, 250);
    window.addEventListener('resize', fixTiles);
  }

  if (document.readyState === 'loading') {
    window.addEventListener('load', init);
  } else {
    init();
  }
})();

